import { LocalStorage } from '@mentimeter/storage';
import type { UserResponseT, Regions } from '@mentimeter/http-clients';
import { SESSION_TOKEN_KEY, REGION_KEY } from '@mentimeter/user/session';
import { captureException } from '@mentimeter/errors/sentry';
import { MentiError } from '@mentimeter/error-utils/error';

declare global {
  interface Window {
    Intercom: any;
  }
}

export const USER_LOCALSTORAGE_KEY = 'user';

export const getLocalUser = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  return LocalStorage.getJSONItem<UserResponseT>(USER_LOCALSTORAGE_KEY);
};

export const setUserLocalStorageAndCookies = async (
  user: UserResponseT,
): Promise<void> => {
  const sessionToken = user.session_token;
  if (sessionToken !== undefined) {
    await setSessionCookies(sessionToken, user.region);

    storeSessionTokenToLocalStorage({ sessionToken, region: user.region });

    delete user.session_token;
  }

  setUserObjectInLocalStorage(user);
};

export const storeSessionTokenToLocalStorage = ({
  sessionToken,
  region,
}: {
  sessionToken: string;
  region: string;
}) => {
  LocalStorage.setItem({
    type: 'necessary',
    key: SESSION_TOKEN_KEY,
    value: sessionToken,
  });
  LocalStorage.setItem({
    type: 'necessary',
    key: REGION_KEY,
    value: region,
  });
};

export const setUserObjectInLocalStorage = (user: UserResponseT) => {
  LocalStorage.setJSONItem<UserResponseT>({
    type: 'necessary',
    key: USER_LOCALSTORAGE_KEY,
    value: user,
  });
};

export const getSessionToken = (
  user?: UserResponseT | null,
): string | null | undefined => {
  if (typeof window === 'undefined') {
    return null;
  }
  const sessionToken = LocalStorage.getItem(SESSION_TOKEN_KEY);
  if (sessionToken !== null) {
    return sessionToken;
  } else if (
    user &&
    user.identifier !== undefined &&
    user.identifier !== null &&
    user.id !== undefined &&
    user.id !== null
  ) {
    return `${user.identifier}.${user.id}`;
  }
  return null;
};

export const getAuthorizationBearer = () => {
  return `Bearer ${getSessionToken()}`;
};

export const removeLocalUser = () => {
  LocalStorage.removeItem(USER_LOCALSTORAGE_KEY);
};

const removeSessionToken = () => {
  LocalStorage.removeItem(SESSION_TOKEN_KEY);
  LocalStorage.removeItem(REGION_KEY);
};

export const intercomLogout = () => {
  try {
    window.Intercom('shutdown');
  } catch (_e) {
    // Maybe manually delete "intercom-" cookies?
  }
};

export const removeLocalUserAndSessionToken = async () => {
  removeSessionToken();
  removeLocalUser();
  await removeSessionCookies();
};

const removeSessionCookies = async () => {
  try {
    await fetch('/auth/api/logout', {
      method: 'POST',
    });
  } catch (error) {}
};

export function setSessionCookies(sessionToken: string, region: Regions) {
  try {
    return fetch('/auth/api/session', {
      body: JSON.stringify({
        sessionToken,
        region,
      }),
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    });
  } catch (e) {
    captureException(
      new MentiError('Set Session Token Error', {
        cause: e,
        feature: 'platform',
      }),
    );

    throw e;
  }
}
